import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Building2, Phone, Mail, Send, Loader2, CheckCircle,
  Truck, ShieldCheck, Clock, Users, Store, Award
} from 'lucide-react';
import { whatsappNumber } from '../data/mockData';
import { toast } from 'sonner';

// Partenaires GMS (texte uniquement - pas de logos externes)
const gmsPartners = ['Carrefour', 'Prosuma', 'CDCI'];

const B2B = () => {
  const { language } = useLanguage();
  const t = language === 'fr';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    contact: '',
    email: '',
    phone: '',
    type: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subject: `[B2B] Demande de ${formData.company}`
        }),
      });
      
      if (!response.ok) throw new Error('Error');
      
      toast.success(t ? 'Demande envoyée !' : 'Request sent!', {
        description: t ? 'Notre équipe commerciale vous contactera sous 24h.' : 'Our sales team will contact you within 24h.',
      });
      
      setFormData({ company: '', contact: '', email: '', phone: '', type: '', message: '' });
    } catch (error) {
      toast.error(t ? 'Erreur' : 'Error', {
        description: t ? 'Veuillez réessayer ou nous appeler directement.' : 'Please try again or call us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientTypes = t ? [
    { value: 'hotel', label: 'Hôtel' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'traiteur', label: 'Traiteur' },
    { value: 'supermarket', label: 'Supermarché' },
    { value: 'collectivite', label: 'Collectivité' },
    { value: 'other', label: 'Autre' }
  ] : [
    { value: 'hotel', label: 'Hotel' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'traiteur', label: 'Caterer' },
    { value: 'supermarket', label: 'Supermarket' },
    { value: 'collectivite', label: 'Institution' },
    { value: 'other', label: 'Other' }
  ];

  const advantages = [
    {
      icon: Truck,
      title: t ? 'Livraison quotidienne' : 'Daily delivery',
      desc: t ? 'Livraison fraîcheur tous les jours ouvrables à Abidjan' : 'Fresh delivery every business day in Abidjan'
    },
    {
      icon: ShieldCheck,
      title: t ? 'Qualité garantie' : 'Guaranteed quality',
      desc: t ? 'Mêmes standards que les grandes surfaces' : 'Same standards as major retailers'
    },
    {
      icon: Clock,
      title: t ? 'Commande flexible' : 'Flexible ordering',
      desc: t ? 'Commandez avant 16h pour livraison le lendemain' : 'Order before 4pm for next-day delivery'
    },
    {
      icon: Users,
      title: t ? 'Compte dédié' : 'Dedicated account',
      desc: t ? 'Un commercial dédié pour vos besoins' : 'A dedicated sales rep for your needs'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F2E8] dark:bg-[#0F1A14]">
      {/* Hero */}
      <section className="relative py-16 md:py-20 bg-[#1A5C38] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.5" fill="white" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
        
        <div className="container relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 bg-white/10 text-white/90 px-4 py-1 rounded-full text-sm mb-4">
              <Building2 size={16} />
              {t ? 'Espace Professionnels' : 'Business Area'}
            </span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            {t ? 'Approvisionnement B2B' : 'B2B Supply'}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/80 max-w-xl mx-auto mb-8"
          >
            {t 
              ? 'Hôtels, restaurants, traiteurs, collectivités — nous fournissons les professionnels les plus exigeants d\'Abidjan.'
              : 'Hotels, restaurants, caterers, institutions — we supply the most demanding professionals in Abidjan.'}
          </motion.p>

          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#E09000] text-[#1A5C38] font-semibold px-6 py-3 rounded-full transition-all"
          >
            <Phone size={18} />
            {t ? 'Appeler un commercial' : 'Call sales'}
          </motion.a>
        </div>
      </section>

      {/* GMS Partners - Credibility */}
      <section className="py-8 bg-white dark:bg-[#1A2B22] border-b border-gray-100 dark:border-gray-800">
        <div className="container">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t ? 'Ils nous font confiance' : 'They trust us'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {gmsPartners.map((partner, i) => (
              <span key={i} className="text-[#1A5C38] dark:text-gray-300 font-semibold text-sm md:text-base">
                {partner}
              </span>
            ))}
            <span className="text-gray-400 text-sm">|</span>
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <Store size={18} />
              <span className="text-sm">{t ? 'Hôtels 5 étoiles' : '5-star Hotels'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-12 md:py-16">
        <div className="container">
          <h2 className="text-2xl font-bold text-[#1A5C38] dark:text-white text-center mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
            {t ? 'Pourquoi choisir Océana ?' : 'Why choose Océana?'}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-[#1A2B22] p-6 rounded-2xl shadow-sm"
              >
                <div className="w-12 h-12 bg-[#1A5C38]/10 dark:bg-[#1A5C38]/30 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="text-[#1A5C38] dark:text-[#2D8A56]" size={24} />
                </div>
                <h3 className="font-semibold text-[#1A5C38] dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 md:py-16 bg-white dark:bg-[#1A2B22]">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#1A5C38] dark:text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t ? 'Demander un devis' : 'Request a quote'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {t ? 'Remplissez le formulaire, nous vous répondons sous 24h.' : 'Fill the form, we respond within 24h.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-[#F8F2E8] dark:bg-[#0F1A14] rounded-2xl p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t ? 'Entreprise *' : 'Company *'}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#1A2B22] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t ? 'Nom du contact *' : 'Contact name *'}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact}
                    onChange={(e) => setFormData({...formData, contact: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#1A2B22] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#1A2B22] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t ? 'Téléphone *' : 'Phone *'}
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#1A2B22] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t ? 'Type d\'établissement *' : 'Business type *'}
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#1A2B22] dark:text-white focus:outline-none focus:border-[#1A5C38]"
                >
                  <option value="">{t ? 'Sélectionner...' : 'Select...'}</option>
                  {clientTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t ? 'Vos besoins' : 'Your needs'}
                </label>
                <textarea
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder={t ? 'Décrivez vos besoins (volumes, fréquence, produits...)' : 'Describe your needs (volumes, frequency, products...)'}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-[#1A2B22] dark:text-white focus:outline-none focus:border-[#1A5C38] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1A5C38] hover:bg-[#145230] text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                {t ? 'Envoyer la demande' : 'Send request'}
              </button>
            </form>

            {/* Direct Contact */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {t ? 'Besoin d\'une réponse immédiate ?' : 'Need an immediate response?'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href={`tel:${whatsappNumber}`}
                  className="inline-flex items-center gap-2 text-[#1A5C38] dark:text-[#2D8A56] hover:underline"
                >
                  <Phone size={16} />
                  {whatsappNumber}
                </a>
                <a 
                  href="mailto:b2b@oceana-ci.com"
                  className="inline-flex items-center gap-2 text-[#1A5C38] dark:text-[#2D8A56] hover:underline"
                >
                  <Mail size={16} />
                  b2b@oceana-ci.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2B;
